import { useState, useRef } from 'react'
import { Modal } from '@/shared/components/Modal'
import { api } from '@/lib/api'
import { trackEvent } from '@/lib/telemetry'
import type { ModalName } from '@/lib/modalTelemetry'

interface ComingSoonConfig {
  feature: string
  /**
   * Which dialog this is, for telemetry. Separate from `feature` (which is a URL path segment for
   * POST /api/interest/:feature) so the telemetry name stays a literal from the closed union.
   */
  track: ModalName
  // Retained for call-site compatibility; dedupe now keys off `feature` in
  // localStorage (persists across sessions) rather than this per-session key.
  sessionKey?: string
  title: string
  description: string
}

// Per-feature "registered interest" flag, remembered across sessions. Defensive
// try/catch mirrors the app's other localStorage helpers (tm-welcome-seen,
// tm-selected-org) so Safari private browsing never throws.
const interestKey = (feature: string) => `tm-interest-${feature}`

function hasRegistered(feature: string): boolean {
  try {
    return localStorage.getItem(interestKey(feature)) === '1'
  } catch {
    return false
  }
}

function markRegistered(feature: string): void {
  try {
    localStorage.setItem(interestKey(feature), '1')
  } catch {
    /* storage unavailable - ignore */
  }
}

/**
 * "Coming soon" modal + interest registration. openModal() shows the modal and,
 * the first time a user registers interest in a feature (tracked in localStorage,
 * so it persists across sessions), fires two fire-and-forget signals so we can
 * gauge demand: a `register_interest` tracking event and a POST /api/interest/:feature.
 * On return visits the modal acknowledges that they are already on the list and
 * re-fires nothing. Returns the modal element to render and the opener.
 */
export function useComingSoon({ feature, track, title, description }: ComingSoonConfig) {
  const [show, setShow] = useState(false)
  const hasFiredRef = useRef(false)
  // Snapshot the already-registered state at open time. Capturing it here (rather
  // than reading localStorage during render) keeps the FIRST open showing the real
  // description: openModal marks the feature registered synchronously, so a render
  // read would flip to the acknowledgement copy on that same first open.
  const [alreadyRegistered, setAlreadyRegistered] = useState(false)

  function openModal() {
    const registeredBefore = hasRegistered(feature)
    setAlreadyRegistered(registeredBefore)
    setShow(true)
    // Register once ever (localStorage), and guard against a double-fire within
    // this mount.
    if (!hasFiredRef.current && !registeredBefore) {
      hasFiredRef.current = true
      markRegistered(feature)
      trackEvent('register_interest', feature)
      api.post(`/api/interest/${feature}`).catch(() => {})
    }
  }

  const modal = show ? (
    <Modal track={track} title={title} kicker="Coming Soon" onClose={() => setShow(false)} maxWidth={400}>
      <p style={{ fontFamily: 'var(--body)', fontSize: '0.85rem', color: 'var(--text2)', lineHeight: 1.6, margin: 0 }}>
        {alreadyRegistered ? "You're already on the list - we'll let you know when it ships." : description}
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button className="btn btn-sm" onClick={() => setShow(false)}>
          {alreadyRegistered ? 'Close' : 'Got it'}
        </button>
      </div>
    </Modal>
  ) : null

  return { openModal, modal }
}
